import apiClient from '@app/config/axios.config'
import { Endpoint } from '@app/config/endpoints'
import type { UserProfile } from '@app/types'
import { logger } from '@app/utils/logger'

/**
 * Sends a login request to the API using the provided email and password.
 *
 * @param {string} email - The user's email address.
 * @param {string} uuid - The user's uuid.
 * @returns {Promise<object>} The response data from the login API, including user_uuid from headers.
 */

const AuthService = {
  incrementLoginCounter: async (user_uuid: string | undefined) => {
    try {
      const response = await apiClient.post(Endpoint.IncrementLoginCount, {
        user_uuid,
        client_name: window.localStorage.getItem('client_name'),
      })
      return response.data
    } catch (error) {
      logger.error('Error incrementing login counter:', error)
    }
  },

  user: async (): Promise<UserProfile | null> => {
    try {
      const userResponse = await apiClient.get(Endpoint.getUser)
      return userResponse.data
    } catch (error) {
      logger.error('Error fetching user data:', error)
      throw error
    }
  },

  signup: async (data: { email: string }): Promise<{ redirectUrl: string }> => {
    const response = await apiClient.post(Endpoint.Signup, data)
    return response.data
  },

  accept_terms: async (data: { user_info: { user_uuid: string; client_name: string } }) => {
    try {
      const accept_terms_and_conditions = await apiClient.post(Endpoint.AcceptTerms, data)
      return accept_terms_and_conditions.data
    } catch (error) {
      logger.error('Error accepting terms and conditions:', error)
      throw new Error('Failed to accept terms and conditions')
    }
  },
}

export default AuthService
